import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUser, takeBootOnboardingCheck } from '../contexts/UserContext';
import { useBrand } from '../contexts/BrandContext';
import { BrandName } from '../components/Common/BrandedText';
import StrictAuthGuard from './StrictAuthGuard';
import ConnectionProblemScreen from './Common/ConnectionProblemScreen';
import OnboardingWelcome from './OnboardingWelcome';
import OnboardingWizard from './OnboardingWizard';
import AssessmentStep from './Onboarding/AssessmentStep';
import { supabase } from '../utils/supabaseClient';
import { isAppOwner, isAdmin } from '../utils/roleUtils';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, connectionProblem } = useUser();
  const location = useLocation();
  const { brand } = useBrand();
  const [onboardingStatus, setOnboardingStatus] = useState('checking');
  // Setup opens on the two questions, not on a page comparing Quick vs Complete. That
  // comparison existed when the cards WERE the choice; now the questions make it, so
  // presenting two options first and then not letting the user pick is worse than the
  // toggle it replaced. OnboardingWelcome is kept for entry points that pass a mode.
  const [showWelcome, setShowWelcome] = useState(false);
  // THE CHOSEN MODE SURVIVES A PAGE LOAD. It was plain state, so any full navigation out
  // of the app and back remounted this component, reset the mode to null, and dropped the
  // user on the "Let's get you set up" chooser again -- whatever they had already done.
  //
  // Stripe Connect is the case that makes it obvious. Connect onboarding leaves the site
  // entirely and returns to /settings?tab=payments, which this gate intercepts because
  // onboarding is still 'needed'. The contractor finishes the hardest part of setup at
  // Stripe and lands back at question one, with no sign their progress survived.
  //
  // Keyed per company so switching accounts cannot inherit the other one's answer, and
  // read lazily so the very first render already has it -- a useEffect would flash the
  // chooser for a frame before correcting itself.
  const onboardingModeKey = user?.company_id ? `onboarding_mode_${user.company_id}` : null;
  const [onboardingMode, setOnboardingModeState] = useState(null);
  // RESTORED IN AN EFFECT, NOT IN THE useState INITIALISER. The initialiser runs once, at
  // mount, and `user` is still loading then -- so onboardingModeKey was null, the saved
  // mode was never read, and returning from Stripe still landed on the chooser. That was
  // the first version of this fix, and it did not work for exactly the reason it looked
  // like it should: the key depends on data that arrives later.
  //
  // modeResolved gates the render below so the chooser cannot flash before the restore has
  // been attempted. Nothing is lost by waiting: onboardingStatus is 'complete' until
  // company_id exists, so this gate is not on screen during that window anyway.
  const [modeResolved, setModeResolved] = useState(false);
  useEffect(() => {
    if (!onboardingModeKey || !user?.company_id) return;
    let cancelled = false;

    (async () => {
      let resolved = null;
      try {
        const saved = localStorage.getItem(onboardingModeKey);
        if (saved === 'quick' || saved === 'complete') resolved = saved;
      } catch { /* private mode: no local cache, fall through to the database */ }

      // THE DATABASE IS THE SOURCE OF TRUTH; localStorage is only a cache that saves a
      // round-trip. The choice was being kept in two places — this key, and
      // onboarding_progress.assessment.chosen_mode, which AssessmentStep has always written —
      // and only the browser copy was ever read back. So clearing a browser, switching
      // machines, or leaving and using Continue Setup dropped the user into the six-step
      // Guided flow while the database still recorded the mode they picked. Reported as
      // Continue Setup forgetting a Quick flow; the row said chosen_mode all along.
      if (!resolved) {
        try {
          const { data } = await supabase
            .from('company_settings')
            .select('onboarding_progress')
            .eq('company_id', user.company_id)
            .maybeSingle();
          const stored = data?.onboarding_progress?.assessment?.chosen_mode;
          if (stored === 'quick' || stored === 'complete') {
            resolved = stored;
            // Re-seed the cache so the next load does not pay for this again.
            try { localStorage.setItem(onboardingModeKey, stored); } catch { /* no-op */ }
          }
        } catch { /* unreachable: ask the question rather than guess a flow */ }
      }

      if (cancelled) return;
      if (resolved) setOnboardingModeState(resolved);
      setModeResolved(true);
    })();

    return () => { cancelled = true; };
  }, [onboardingModeKey, user?.company_id]);
  const setOnboardingMode = (mode) => {
    setOnboardingModeState(mode);
    try {
      if (!onboardingModeKey) return;
      if (mode) localStorage.setItem(onboardingModeKey, mode);
      else localStorage.removeItem(onboardingModeKey);
    } catch { /* private mode: fall back to in-memory only */ }
  };

  // Check if user should bypass onboarding (app_owner only)
  const shouldBypassOnboarding = () => isAppOwner(user);

  // Check if onboarding is complete
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (shouldBypassOnboarding()) {
        console.log('🔑 App owner detected - bypassing onboarding');
        setOnboardingStatus('complete');
        return;
      }

      if (!user?.company_id) {
        setOnboardingStatus('complete');
        return;
      }

      // Onboarding CONFIGURES THE COMPANY — business details, services, pricing,
      // financial setup, and inviting more staff. That is an owner/admin task, so
      // everyone else goes straight into the app.
      //
      // This check did not exist, and onboarding_progress is keyed by company_id with NO
      // user_id (verified live 2026-08-29), so the wizard is one shared position per
      // company rather than per person. Combined, an invited technician who clicked their
      // invite link was dropped into the OWNER'S half-finished setup at the owner's
      // current step, able to edit company basics, services and pricing and invite
      // further staff. Reported by the operator 2026-08-29 after inviting a technician.
      //
      // Deliberately isAdmin (owner/admin) rather than a settings permission: the wizard
      // writes company-wide configuration, and a Crew-tier seat (technician, apprentice,
      // helper) has no business in it. 'complete' here means "do not show the wizard to
      // THIS user" — the company's own progress row is untouched, so the owner still
      // resumes exactly where they left off.
      if (!isAdmin(user)) {
        setOnboardingStatus('complete');
        return;
      }

      try {
        // The start-up check may already have asked (page speed, 2026-10-25); same RPC.
        const { data, error } = await (takeBootOnboardingCheck(user.company_id)
          || supabase.rpc('is_onboarding_complete', {
            p_company_id: user.company_id
          }));

        if (error) {
          console.warn('⚠️ Onboarding check failed, assuming complete:', error.message);
          setOnboardingStatus('complete');
          return;
        }

        setOnboardingStatus(data ? 'complete' : 'needed');
      } catch (error) {
        console.warn('⚠️ Onboarding check error, assuming complete:', error);
        setOnboardingStatus('complete');
      }
    };

    if (isAuthenticated && user) {
      checkOnboardingStatus();
    }
  }, [isAuthenticated, user]);

  // NOTE: this used to force-redirect APP_OWNER to /admin-dashboard on every load. Platform
  // tooling now lives in the standalone Platform Console (apps/platform-console), so a platform
  // owner signed into the contractor app is here to USE the contractor app — trapping them on a
  // dashboard route would break that. Onboarding bypass above still applies.

  // 2026-10-23: the start-up check could not reach the server (or is still waiting on it).
  // Before this, a dropped connection sent a signed-in user to the login form and a stalled
  // one left this spinner up for good.
  if (connectionProblem && (loading || !isAuthenticated)) {
    return <ConnectionProblemScreen />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading <BrandName />...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Board #3181, approved 2026-09-24. This used to render <Login /> IN PLACE, which left
    // the address bar on the protected path: sign out from /dashboard and you were looking
    // at a login form at /dashboard, and there was no URL a signed-out user could ever land
    // on. It also stranded Login's ?redirect= handling, which had no producer — so signing
    // in from a deep link never returned you to it.
    //
    // Only the path is carried, and Login validates it as same-origin before using it.
    const dest = `${location.pathname}${location.search}${location.hash}`;
    const wantsReturn = dest && dest !== '/' && dest !== '/dashboard';
    return (
      <Navigate
        replace
        to={wantsReturn ? `/login?redirect=${encodeURIComponent(dest)}` : '/login'}
      />
    );
  }

  if (onboardingStatus === 'checking') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking setup status...</p>
        </div>
      </div>
    );
  }

  if (onboardingStatus === 'needed') {
    // The welcome screen no longer chooses the path. It used to offer "Quick Start" vs
    // "Complete Setup" as two cards, which asks somebody who has never seen the product to
    // predict which one they need. AssessmentStep asks two concrete questions and picks.
    // A mode may still be passed (deep links, tests); honour it and skip the questions.
    const handleStartOnboarding = (mode) => {
      if (mode === 'quick' || mode === 'complete') setOnboardingMode(mode);
      setShowWelcome(false);
    };

    const handleOnboardingComplete = () => {
      console.log('🎉 Onboarding completed successfully!');
      setOnboardingStatus('complete');
    };

    // Skipping has to PERSIST. This used to set React state and nothing else, so
    // is_onboarding_complete still answered false and the next refresh, new tab or login
    // dropped the contractor straight back in. That is the only way out of the wizard —
    // it is rendered by ProtectedRoute, which wraps every protected route, so while it is
    // showing no URL escapes it.
    //
    // skipped_at is deliberately NOT completed_at: dismissing setup and finishing it are
    // different facts, and the RPC now accepts either (migration 20260830).
    const handleSkip = async () => {
      // Dismiss locally first — a failed write must never trap the user in the wizard,
      // which is the very thing being fixed.
      setOnboardingStatus('complete');
      // The remembered mode SURVIVES a skip. It used to be cleared here, on the reasoning
      // that a later visit should start from the question rather than silently resume a path
      // the user walked away from — but the database kept chosen_mode regardless, so the two
      // disagreed and Continue Setup reopened the Guided flow for someone who had chosen
      // Quick. Resuming where you left off is what "Continue Setup" promises; changing your
      // mind is what the Switch setup mode control below is for.
      try {
        if (!user?.company_id) return;
        const { data } = await supabase
          .from('company_settings')
          .select('onboarding_progress')
          .eq('company_id', user.company_id)
          .maybeSingle();

        await supabase
          .from('company_settings')
          .update({
            onboarding_progress: {
              ...(data?.onboarding_progress || {}),
              skipped_at: new Date().toISOString()
            },
            updated_at: new Date().toISOString()
          })
          .eq('company_id', user.company_id);
      } catch (err) {
        console.warn('Could not persist onboarding skip:', err?.message || err);
      }
    };

    if (showWelcome) {
      return (
        <OnboardingWelcome
          onStartOnboarding={handleStartOnboarding}
          onSkip={handleSkip}
        />
      );
    }

    // Wait for the restore attempt before asking the question again. Rendering the
    // chooser first and correcting it a frame later is what makes a returning user
    // believe their setup was lost.
    if (!modeResolved) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-900">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (!onboardingMode) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4 py-10">
          <AssessmentStep onChoose={setOnboardingMode} onSkip={handleSkip} />
        </div>
      );
    }

    return (
      <OnboardingWizard
        onComplete={handleOnboardingComplete}
        onSkip={handleSkip}
        // Back to the chooser. The local cache is cleared with it; the database still holds
        // the old chosen_mode until AssessmentStep writes the new one, which happens the
        // moment a choice is made.
        onChangeMode={() => setOnboardingMode(null)}
        quickStartMode={onboardingMode === 'quick'}
      />
    );
  }

  return (
    <StrictAuthGuard>
      {children}
    </StrictAuthGuard>
  );
};

export default ProtectedRoute;
